import { useMutation, useQuery, useQueryClient } from "@tanstack/vue-query";
import { queryClient } from "./queryClient";
import { invitationApi } from "@/api/invitation.api";
import { userApi } from "@/api/user.api";
import { storeToRefs } from "pinia";
import { ScopeRef, ScopeRefKind } from "@/types/member";

export function useInviteInCampaign() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: { campaignId: string, email: string, firstName: string, lastName: string }) => {
      await userApi.createAccount(data.email, data.firstName, data.lastName, false);
      await invitationApi.inviteInCampaign(data.campaignId, data.email);
    },
    onSuccess: (data, variables) => queryClient.invalidateQueries({ queryKey: ['participations', { campaignId: variables.campaignId }] }),
  });
}

export function useInviteInOrganisation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: {
      email: string,
      firstName: string,
      lastName: string,
      roleId: string,
      scopes: ScopeRef[],
      sendEmail: boolean,
    }) => {
      await userApi.createAccount(data.email, data.firstName, data.lastName, false);
      await invitationApi.inviteInOrganisation(
        data.email,
        data.roleId,
        data.scopes,
        data.sendEmail,
      );
    },
    onSuccess: (data, variables) => queryClient.invalidateQueries({ queryKey: ['members'] }),
  });
}

export function useCampaignPublicInvitation(campaignId?: string) {
  const appStore = storeToRefs(useAppStore());
  return useQuery({
    queryKey: ['campaigns', { organisationId: appStore.currentOrganisationId, spaceId: appStore.currentSpaceId, campaignId: campaignId ? campaignId : appStore.currentCampaignId }, "public_invitation"],
    queryFn: () => invitationApi.getInvitationFromCampaign(campaignId ? campaignId :  appStore.currentCampaignId.value),
    enabled: !!toValue(campaignId ? campaignId : appStore.currentCampaignId) && !!toValue(appStore.currentSpaceId) && !!toValue(appStore.currentOrganisationId),
    retry: false,
  });
}

export function useEnablePublicInvitation() {
  const appStore = storeToRefs(useAppStore());
  return useMutation({
    mutationFn: (data: { campaignId: string }) => invitationApi.createCampaignPublicInvitation(data.campaignId),
    onSuccess: (_, { campaignId }) => {
      queryClient.invalidateQueries({ queryKey: ['campaigns', { organisationId: appStore.currentOrganisationId, spaceId: appStore.currentSpaceId, campaignId: campaignId }, "public_invitation"] })
    }
  });
}

export function useDisablePublicInvitation() {
  const appStore = storeToRefs(useAppStore());
  return useMutation({
    mutationFn: (data: { invitationId: string }) => invitationApi.deleteCampaignInvitation(data.invitationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaigns', { organisationId: appStore.currentOrganisationId, spaceId: appStore.currentSpaceId }, "public_invitation"] })
    }
  });
}